/**
 * <LastAssessed> — the provenance line that replaces the ad-hoc "Scan now /
 * Refresh" buttons across posture surfaces.
 *
 * Every posture scan runs on an automatic cadence (~2×/day), so the question
 * a user has is "how fresh is this?", not "should I poke it?". Each surface
 * leads with *who* assessed it and *when*, as a Carbon helper-text line led
 * by the iuvo mark (DESIGN.md "Do": a provenance line under the verdict):
 *
 *     Assessed by Guardian at 06:12 today · Litware Health · refreshes twice daily
 *
 * The actor is effectively always Guardian, so `by` defaults to it; a real
 * actor is passed only where the response already carries one (the Drift
 * surface's scanJob.createdBy).
 *
 * Intentionally PURE / presentational — no data fetching, no effects — so it
 * renders deterministically under the project's renderToStaticMarkup test
 * harness (see reference: web component test harness).
 */

import clsx from 'clsx'
import { IuvoMark } from '@/components/carbon/IuvoMark'
import { StatusDot } from '@/components/carbon/StatusDot'

export interface LastAssessedProps {
  /** ISO timestamp of the last assessment. null/undefined → "Awaiting first assessment". */
  at: string | null | undefined
  /** Human cadence phrase, e.g. "twice daily", "daily", "automatically". Omit to hide. */
  cadence?: string
  /** Who performed the assessment. Defaults to "Guardian". */
  by?: string
  /** What was assessed ("Litware Health", "186 settings"). Omit to hide. */
  scope?: string
  /** Health of the last assessment, for surfaces that expose it (e.g. User Analytics). */
  status?: 'ok' | 'running' | 'failed'
  /** Extra classes for the host layout. */
  className?: string
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/**
 * The clock phrase after "Assessed by …": "at 06:12 today", "at 22:05
 * yesterday", else "on Oct 2 at 06:12". Null for an unparseable value.
 */
export function formatAssessedClock(at: string, now: Date = new Date()): string | null {
  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return null
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  if (days <= 0) return `at ${time} today`
  if (days === 1) return `at ${time} yesterday`
  return `on ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`
}

const STATUS_LABEL = {
  running: 'Assessment running',
  failed: 'The last assessment failed',
} as const

export default function LastAssessed({
  at,
  cadence,
  by = 'Guardian',
  scope,
  status,
  className = '',
}: LastAssessedProps) {
  const clock = at ? formatAssessedClock(at) : null
  const title = clock && at ? new Date(at).toLocaleString() : undefined
  const parts = [
    clock ? `Assessed by ${by} ${clock}` : at ? `Assessed by ${by}` : 'Awaiting first assessment',
    scope,
    cadence ? `refreshes ${cadence}` : undefined,
  ].filter(Boolean)

  return (
    <span className={clsx('inline-flex items-center gap-2 text-helper text-text-helper', className)} title={title}>
      <IuvoMark size={14} />
      {status === 'running' || status === 'failed' ? (
        <StatusDot tone={status === 'failed' ? 'crit' : 'active'} label={STATUS_LABEL[status]} />
      ) : null}
      <span>{parts.join(' · ')}</span>
    </span>
  )
}
